import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject } from "rxjs";

interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: any;
  [props: string]: any;
}

interface IUpdateHouseInterface {
  title: string;
  house_id: number;
  cover?: Object;
}

interface IGetOverbookingDetailsInterface {
  link_id: string;
  link_type: string;
}

interface ISetOverbookingInterface {
  link_id: number;
  link_type: string;
  is_allowed_overbooking: number;
  is_stop_managing_inventory: number;
  date_list?: Array<Object>;
  dates?: Array<string>;
  is_link_sub_house?: boolean;
  thirdparty_date_list?: Array<Object>;
  thirdparty_dates?: Array<Object>;
}

interface IEditBasicPriceInterface {
  type: number;
  basic_prices_list: string;
}

interface IGetHouseDetailsInterface {
  house_id: number;
}

interface IShelfHouse {
  house_price_id?: number;
  status: number;
  house_type_price_id?: number;
}

interface IReadThirdpartyHouse {
  house_id: number;
}

interface IUnbindSubHouseList {
  house_title?: string;
  page?: number;
  page_size?: number;
  master_house_id: number;
}

interface ISetSubHouse {
  master_house_id: number;
  house_ids: Array<number>;
}

export type Page = "basic" | "rules" | "linked";
export interface PageData {
  page: Page;
  data: any;
}

export interface OperationItem {
  page: Page;
}

const UPDATE_HOUSE_API = "/api/house/update";
const GET_OVERBOOKING_DETAILS_API = "/api/overbooking/details";
const SET_OVERBOOING_API = "/api/overbooking/set";
const UNBIND_SUB_HOUSE_LIST_API = "api/overbooking/unbind_sub_house_list";
const EDIT_BASIC_PRICE_API = "/api/calendar/edit_basic_price";
const GET_HOUSE_DETAILS_API = "/api/house/details";
const Quick_Location_Date_API = "/api/calendar/quick_location_date";
const SHELF_HOUSE_API = "/api/house/shelf";
const READ_THIRDPARTY_HOUSE_API = "/api/house/read_thirdparty_house";
const SET_SUB_HOUSE_API = "/api/overbooking/set_sub_house";
const UPLOAD_TO_ZHUKEYUN_API = "/api/thirdparty/zhukeyun_upload";
const STOP_DETAIL_API = "/api/house/stop_detail";
const UPDATE_STOP_STATUS_API = "/api/house/save_stop";
@Injectable({
  providedIn: "any",
})
export class PropertyDetailService {
  /**
   * 用于处理从各个页面打开房源详情页时，各种需求的变化
   */
  pageDataChange$ = new BehaviorSubject<PageData>(null);

  operationList$ = new BehaviorSubject<any>([]);

  constructor(private http: HttpClient) {}

  changePageData(data: PageData) {
    this.pageDataChange$.next(data);
  }

  operate(opetionList: any[]) {
    this.operationList$.next(opetionList);
  }

  // 更新房间信息：名称、照片
  updateHouse(params: IUpdateHouseInterface) {
    return this.http.post(UPDATE_HOUSE_API, params) as Observable<IHttpResponse>;
  }

  // 获取库存管理详情
  getOverbookingDetails(params: IGetOverbookingDetailsInterface) {
    const query = new HttpParams().append("link_id", `${params.link_id}`).append("link_type", `${params.link_type}`);
    return this.http.get(GET_OVERBOOKING_DETAILS_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  // 设置库存管理策略
  setOverbooking(params: ISetOverbookingInterface) {
    return this.http.post(SET_OVERBOOING_API, params) as Observable<IHttpResponse>;
  }

  // 添加子房间列表
  addUnbindSubHouseList(params: IUnbindSubHouseList) {
    const query = new HttpParams()
      .append("house_title", `${params.house_title}`)
      .append("page", `${params.page}`)
      .append("page_size", `${params.page_size}`)
      .append("master_house_id", `${params.master_house_id}`);
    return this.http.get(UNBIND_SUB_HOUSE_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  // 编辑基础价格（包含周末价格）
  editBasicPrice(params: IEditBasicPriceInterface) {
    return this.http.post(EDIT_BASIC_PRICE_API, params) as Observable<IHttpResponse>;
  }

  // 获取房间详情
  getHouseDetails(params: IGetHouseDetailsInterface) {
    const query = new HttpParams().append("house_id", `${params.house_id}`);
    return this.http.get(GET_HOUSE_DETAILS_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  // 批量改价页面日期快速定位
  getQuickLocationDate() {
    const query = new HttpParams();
    return this.http.get(Quick_Location_Date_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  // 房型上下架
  shelfHouse(params: IShelfHouse) {
    return this.http.post(SHELF_HOUSE_API, params) as Observable<IHttpResponse>;
  }

  // 读取三方房源
  readThirdpartyHouse(params: IReadThirdpartyHouse) {
    return this.http.post(READ_THIRDPARTY_HOUSE_API, params) as Observable<IHttpResponse>;
  }

  // 设置子房间
  setSubHouse(params: ISetSubHouse) {
    return this.http.post(SET_SUB_HOUSE_API, params) as Observable<IHttpResponse>;
  }

  requestSetSubHouseV2(params) {
    return this.http.post("/api/overbooking/v2_set_sub_house", params);
  }

  // 停用详情
  getStopDetail(params) {
    return this.http.get(STOP_DETAIL_API, { params }) as Observable<IHttpResponse>;
  }

  updateStopStatus(params) {
    return this.http.post(UPDATE_STOP_STATUS_API, params) as Observable<IHttpResponse>;
  }

  uploadToZhukeyun(params) {
    return this.http.post(UPLOAD_TO_ZHUKEYUN_API, params) as Observable<IHttpResponse>;
  }

  requestApplyToAllResvs(params) {
    return this.http.post("/api/overbooking/handle_existing_reservations", params);
  }
}
